import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { Check } from "./check";
import { cn } from "./cn";

/** The pill itself, without a tone or a size. */
const chipShape =
  "inline-flex w-fit shrink-0 items-center justify-center gap-1.5 overflow-hidden rounded-pill font-medium whitespace-nowrap focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring [&>svg]:pointer-events-none [&>svg:not([class*='size-'])]:size-3 [&>svg]:shrink-0";

/**
 * Each tone's fill, text and 1px ring. Exported so an app's own badge can map
 * its legacy names onto them.
 */
const chipTones = {
  /** White with the chip shadow: neutral, on the page or a surface. */
  neutral: "bg-card text-body shadow-chip",
  /** The quiet fill: neutral on a white card, or a state that is not live. */
  surface: "bg-muted text-body ring-1 ring-border",
  success: "bg-success-tint text-success ring-1 ring-success-border",
  warning: "bg-warning-tint text-warning ring-1 ring-warning-border",
  danger: "bg-danger-tint text-danger ring-1 ring-danger-border",
  info: "bg-info-tint text-info ring-1 ring-info-border",
  /** The service accent in force (`data-service` on an ancestor). */
  service: "bg-service-tint text-service ring-1 ring-service/20",
  /** A service's own accent, where no `data-service` scope applies. */
  formation: "bg-formation-tint text-formation ring-1 ring-formation/20",
  "federal-tax":
    "bg-federal-tax-tint text-federal-tax ring-1 ring-federal-tax/20",
  accounting: "bg-accounting-tint text-accounting ring-1 ring-accounting/20",
  dissolution:
    "bg-dissolution-tint text-dissolution ring-1 ring-dissolution/20",
} as const;

const chipSizes = {
  sm: "h-5 gap-1 px-2 text-2xs leading-none",
  default: "min-h-6.5 px-2.5 py-0.5 text-xs leading-[1.35]",
  lg: "min-h-8 px-3 py-1 text-sm",
} as const;

/**
 * A status pill (Chip.astro): 12px / 500 text on a tinted fill with a 1px
 * ring. Colour never works alone — a chip carries a word, and `dot` or
 * `check` when the state needs a mark.
 */
const chipVariants = cva(chipShape, {
  variants: { tone: chipTones, size: chipSizes },
  defaultVariants: { tone: "neutral", size: "default" },
});

type ChipTone = NonNullable<VariantProps<typeof chipVariants>["tone"]>;

type ChipProps = React.ComponentProps<"span"> &
  VariantProps<typeof chipVariants> & {
    /** A status dot in the chip's own colour, before the label. */
    dot?: boolean;
    /** A check disc before the label (done, accepted). */
    check?: boolean;
  };

function Chip({
  className,
  tone,
  size,
  dot = false,
  check = false,
  children,
  ...props
}: ChipProps) {
  return (
    <span
      data-slot="chip"
      data-tone={tone ?? "neutral"}
      className={cn(chipVariants({ tone, size }), className)}
      {...props}
    >
      {dot ? (
        <span
          aria-hidden="true"
          className="size-1.5 shrink-0 rounded-full bg-current opacity-80"
        />
      ) : null}
      {check ? <Check size={size === "lg" ? 16 : 14} /> : null}
      {children}
    </span>
  );
}

export { Chip, chipShape, chipSizes, chipTones, chipVariants };
export type { ChipProps, ChipTone };
